import { useEffect } from 'react'
import { useDispatch, useSelector, Provider } from 'react-redux'
import qs from 'qs'
import jump from 'jump.js'

import SearchHead from './components/SearchHead'
import SearchFilters from './components/SearchFilters'
import SearchResult from './components/SearchResult'

import { parseSubjectsFromUrl } from './SearchUtils'

import { itemsFetchData } from './redux/items'
import StructuredSearchData from './components/StructuredSearchData'
import ScrollButton from './components/ScrollButton'
import { LoginModalProvider } from '../Login/context/LoginModalContext'
import { warningToast } from '../lib/toastAlert'
import { store } from './redux/store'

const normalizedBaseUrl = `${location.protocol}//${App.State.host}`
let canonicalEl = null
let metaRobotsTag = null
const metaDescriptionTag = document.querySelector('meta[name="description"]')

const updateCanonical = (storeState) => {
  let { searchPath } = storeState.items
  const currentFilters = { ...storeState.items.currentFilters }
  if (currentFilters && currentFilters.page) {
    searchPath += `?page=${currentFilters.page}`
  }

  if (canonicalEl) {
    canonicalEl.href = `${normalizedBaseUrl}${searchPath}`
  } else {
    const head = document.getElementsByTagName('head')[0]

    canonicalEl = document.createElement('link')
    canonicalEl.rel = 'canonical'
    canonicalEl.href = `${normalizedBaseUrl}${searchPath}`
    head.appendChild(canonicalEl)
  }
}

store.subscribe(() => {
  const storeState = store.getState()

  if (App.State.isBot && storeState.items.hasErrored) {
    window.location.href = '/something-wrong'
  }

  if (storeState.items.isLoading) {
    document.title = '...'
    metaDescriptionTag.setAttribute('content', '')
  }

  if (!storeState.items.isLoading && storeState.items.pageTitle) {
    document.title = storeState.items.pageTitle
  }

  if (!storeState.items.isLoading && storeState.items.metaDescription) {
    metaDescriptionTag.setAttribute('content', storeState.items.metaDescription)
  }

  if (!storeState.items.isLoading && !storeState.items.hasErrored) {
    const isCityPage = storeState.items.searchLabels?.some(
      (label) => label.field === 'city'
    )

    if (
      ((storeState.items.total > 0 && storeState.items.data.length >= 1) ||
        storeState.items.footerDescription) &&
      Object.keys(storeState.items.currentFilters).length === 0 &&
      !isCityPage
    ) {
      // document.getElementsByTagName('meta')['robots'].content = 'all'

      if (metaRobotsTag) {
        metaRobotsTag.parentNode.removeChild(metaRobotsTag)
        metaRobotsTag = null
      }
    } else if (metaRobotsTag) {
      metaRobotsTag.setAttribute('content', 'noindex, follow')
    } else {
      metaRobotsTag = document.createElement('meta')
      metaRobotsTag.name = 'robots'
      metaRobotsTag.content = 'noindex, follow'

      document.getElementsByTagName('head')[0].appendChild(metaRobotsTag)
    }

    if (!storeState.items.popstate) {
      let { searchPath } = storeState.items

      if (
        storeState.items.currentFilters &&
        Object.entries(storeState.items.currentFilters).length > 0
      ) {
        searchPath += `?${qs.stringify(storeState.items.currentFilters, {
          arrayFormat: 'brackets',
        })}`
      }

      // eslint-disable-next-line no-restricted-globals
      history.pushState(storeState.items.currentFilters || {}, null, searchPath)
    }

    updateCanonical(storeState)
  }

  if (!storeState.items.isLoading) {
    if (
      App.State.isMobile &&
      localStorage.scrollPosition &&
      !App.State.scrollToTop
    ) {
      setTimeout(() => {
        window.scrollTo({ top: parseInt(localStorage.scrollPosition, 10) })
        localStorage.removeItem('scrollPosition')
      }, 400)
    }

    if (App.State.scrollToTop) {
      App.State.scrollToTop = false

      jump('#search-result', {
        offset: -300,
      })
    }
  }
})

function CarsSearch() {
  const dispatch = useDispatch()

  const initSearchFilters = () => ({
    popstate: 1,
    ...qs.parse(window.location.search.replace(/^\?/, '')),
    subjects: parseSubjectsFromUrl(),
  })

  useEffect(() => {
    if (App.State.carStatusNotification) {
      warningToast({ title: App.State.carStatusNotification })
    }

    dispatch(itemsFetchData({ filters: initSearchFilters() }))

    window.addEventListener('popstate', () => {
      dispatch(itemsFetchData({ filters: initSearchFilters() }))
    })

    if (App.State.isMobile) {
      window.addEventListener('unload', () => {
        localStorage.setItem('scrollPosition', window.scrollTop)
      })
    }
  }, [])

  const structuredSearchData = useSelector(
    (state) => state.items.structuredSearchData || []
  )

  const getCountries = async () => {
    const countries = await App.Request.get('/countries/react_options.json', {
      params: {
        with_count: 1,
      },
    })

    return countries
  }

  const getCategories = async () => {
    const categories = await App.Request.get(
      '/cars/categories_react_options.json'
    )

    return categories
  }

  const getMakes = async () => {
    const makes = await App.Request.get('/manufacturers/react_options.json', {
      params: {
        with_count: 1,
        push_published: 1,
      },
    })

    return makes
  }

  const getRegistrationCountries = async () =>
    // eslint-disable-next-line no-return-await
    await App.Request.get('/countries/react_options.json')

  return (
    <LoginModalProvider>
      <div className="container">
        <SearchHead />
        <SearchFilters
          makes={getMakes}
          countries={getCountries}
          categories={getCategories}
          registrationCountries={getRegistrationCountries}
        />
        <SearchResult />
        {structuredSearchData?.length > 0 && <StructuredSearchData />}
      </div>
    </LoginModalProvider>
  )
}

export default () => (
  <Provider store={store}>
    {window.innerWidth > 1200 && (
      <ScrollButton>
        <div
          className="rounded-circle bg-white shadow-lg"
          style={{
            bottom: '70px',
            right: '30px',
            padding: '10px 20px',
            background: '#555',
            fontSize: '1.4rem',
            fontWeight: 'bold',
          }}
        >
          <i className="fas fa-chevron-up" />
        </div>
      </ScrollButton>
    )}
    <CarsSearch />
  </Provider>
)
